import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDeveloperContext } from '@/hooks/useDeveloperContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Phone, Mail, MessageSquare, User, Calendar, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';

const STAGES = ['all', 'new', 'contacted', 'qualified', 'visit_scheduled', 'site_visit', 'negotiation', 'booked', 'won', 'lost'] as const;
const STAGE_LABEL: Record<string, string> = {
  all: 'All stages', new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  visit_scheduled: 'Visit Scheduled', site_visit: 'Site Visit', negotiation: 'Negotiation',
  booked: 'Booked', won: 'Won', lost: 'Lost',
};

interface Props {
  /** Limit list to leads assigned to current user. Default false → developer-wide. */
  scope?: 'mine' | 'workspace';
}

export function DeveloperCustomersList({ scope = 'workspace' }: Props) {
  const { user } = useAuth();
  const { ctx } = useDeveloperContext();
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<string>('all');
  const [selected, setSelected] = useState<any | null>(null);

  // Project IDs in this developer workspace (used to scope workspace-wide views).
  const { data: projectIds = [] } = useQuery({
    queryKey: ['dev-customer-project-ids', ctx.developerId],
    enabled: !!ctx.developerId && scope === 'workspace',
    queryFn: async () => {
      const { data } = await supabase.from('projects').select('id').eq('developer_id', ctx.developerId!);
      return (data || []).map((p: any) => p.id as string);
    },
  });

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['dev-customers', scope, user?.id, ctx.developerId, projectIds.length],
    enabled: scope === 'mine' ? !!user : !!ctx.developerId,
    queryFn: async () => {
      let q = supabase
        .from('leads')
        .select('id, name, phone, email, message, status, source, created_at, project_id, assigned_to, projects(name)')
        .order('created_at', { ascending: false });
      if (scope === 'mine') q = q.eq('assigned_to', user!.id);
      else if (projectIds.length) q = q.in('project_id', projectIds);
      else return [];
      const { data } = await q;
      return data || [];
    },
  });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return leads.filter((l: any) => {
      if (stage !== 'all' && l.status !== stage) return false;
      if (!s) return true;
      return (
        l.name?.toLowerCase().includes(s) ||
        l.phone?.toLowerCase().includes(s) ||
        l.email?.toLowerCase().includes(s)
      );
    });
  }, [leads, search, stage]);

  const stageBadge = (st: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      qualified: 'bg-cyan-100 text-cyan-700',
      visit_scheduled: 'bg-orange-100 text-orange-700',
      site_visit: 'bg-orange-100 text-orange-700',
      negotiation: 'bg-pink-100 text-pink-700',
      booked: 'bg-purple-100 text-purple-700',
      won: 'bg-green-100 text-green-700',
      lost: 'bg-muted text-muted-foreground',
    };
    return colors[st] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, or email…"
            className="pl-9 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-[180px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1 flex-wrap">
          {(['all', 'new', 'contacted', 'won', 'lost'] as const).map(s => (
            <Button key={s} variant={stage === s ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setStage(s)}>
              {STAGE_LABEL[s]}
            </Button>
          ))}
        </div>
        <div className="ml-auto text-sm text-muted-foreground">{filtered.length} of {leads.length}</div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No customers match your filters.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((l: any) => (
            <Card key={l.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(l)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                      {l.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{l.name || 'Unknown'}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{l.phone}</div>
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${stageBadge(l.status)}`}>{STAGE_LABEL[l.status] || l.status}</Badge>
                </div>
                {l.email && <div className="text-xs text-muted-foreground mt-2 truncate flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</div>}
                {l.projects && (
                  <div className="text-[11px] text-muted-foreground mt-1 truncate">
                    {typeof l.projects.name === 'object' ? l.projects.name?.en : l.projects.name}
                  </div>
                )}
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={(e) => { e.stopPropagation(); l.phone && window.open(`tel:${l.phone}`); }}>
                    <Phone className="h-3 w-3 mr-1" />Call
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={(e) => { e.stopPropagation(); l.phone && window.open(`https://wa.me/${l.phone.replace(/[^0-9]/g, '')}`); }}>
                    <MessageSquare className="h-3 w-3 mr-1" />WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CustomerDrawer lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function CustomerDrawer({ lead, onClose }: { lead: any | null; onClose: () => void }) {
  const open = !!lead;
  const { data: tasks = [] } = useQuery({
    queryKey: ['cust-tasks', lead?.id],
    enabled: open,
    queryFn: async () => {
      const { data } = await supabase
        .from('crm_tasks')
        .select('id, title, notes, due_at, status')
        .eq('lead_id', lead.id)
        .order('due_at', { ascending: true, nullsFirst: false });
      return data || [];
    },
  });
  const { data: activities = [] } = useQuery({
    queryKey: ['cust-activities', lead?.id],
    enabled: open,
    queryFn: async () => {
      const { data } = await supabase
        .from('crm_activities')
        .select('id, type, payload, created_at')
        .eq('lead_id', lead.id)
        .order('created_at', { ascending: false })
        .limit(50);
      return data || [];
    },
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        {lead && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />{lead.name || 'Customer'}
              </SheetTitle>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              {/* Lead info */}
              <Card>
                <CardContent className="p-4 space-y-2 text-sm">
                  <Row label="Stage"><Badge variant="outline">{STAGE_LABEL[lead.status] || lead.status}</Badge></Row>
                  <Row label="Phone">{lead.phone || '—'}</Row>
                  <Row label="Email">{lead.email || '—'}</Row>
                  <Row label="Source">{lead.source || 'website'}</Row>
                  <Row label="Created">{format(new Date(lead.created_at), 'MMM d, yyyy HH:mm')}</Row>
                  {lead.message && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">Message</div>
                      <p className="text-sm whitespace-pre-wrap">{lead.message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tasks */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Calendar className="h-4 w-4" />Tasks ({tasks.length})</h3>
                {tasks.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-2">No tasks linked.</div>
                ) : (
                  <div className="space-y-1.5">
                    {tasks.map((t: any) => (
                      <div key={t.id} className="border rounded-lg p-2 text-xs flex items-start gap-2">
                        <CheckCircle2 className={`h-4 w-4 mt-0.5 ${t.status === 'done' ? 'text-green-600 fill-green-100' : 'text-muted-foreground'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</div>
                          {t.due_at && <div className="text-[10px] text-muted-foreground">Due {format(new Date(t.due_at), 'MMM d, HH:mm')}</div>}
                          {t.notes && <p className="text-[11px] text-muted-foreground mt-0.5">{t.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Activity timeline</h3>
                {activities.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-2">No activity recorded yet.</div>
                ) : (
                  <div className="space-y-2">
                    {activities.map((a: any) => (
                      <div key={a.id} className="border-l-2 border-primary/40 pl-3 py-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{a.type}</Badge>
                          <span className="text-[10px] text-muted-foreground">{format(new Date(a.created_at), 'MMM d, HH:mm')}</span>
                        </div>
                        {a.payload && Object.keys(a.payload).length > 0 && (
                          <pre className="text-[10px] text-muted-foreground mt-1 whitespace-pre-wrap break-all">{JSON.stringify(a.payload)}</pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                {lead.phone && <Button className="flex-1" onClick={() => window.open(`tel:${lead.phone}`)}><Phone className="h-4 w-4 mr-1" />Call</Button>}
                {lead.email && <Button variant="outline" className="flex-1" onClick={() => window.open(`mailto:${lead.email}`)}><Mail className="h-4 w-4 mr-1" />Email</Button>}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{children}</span>
    </div>
  );
}
